/**
 * The item's conversation (docs/ux/project-tab-spec.md §1.3a): a streaming chat with the
 * project's triage agent. Committed messages come from the sealed item; the reply in progress
 * streams from the item session's encrypted run events. Proposal cards, a pinned action list,
 * manual "Split into new item from here", explicit-marker splits and Undo split.
 */
import type { BarkRecord, BarkThreadMessage } from '@viko/protocol'
import { Button, Markdown, type TextItem } from '@viko/transcript-ui'
import { Mic, MicOff, Scissors, Send } from 'lucide-react'
import { useId, useState } from 'react'
import { LARGE_TEXT_BYTES, useAttachmentInput } from '../../lib/attachment-input.js'
import type { BarkStore } from '../../lib/bark-store.js'
import { actionList, isDoIt, latestOpenProposal, startsNewTopic } from '../../lib/discuss-store.js'
import { useRunTranscript } from '../../lib/session.js'
import { useDictation } from '../../lib/speech.js'
import { AttachmentDropZone } from '../AttachmentDropZone.js'
import { storage } from '../Capture.js'
import { errorText, useToast } from '../ui.js'
import { ProposalCard } from './ProposalCard.js'
import { BelongsTo, SegmentChip } from './SegmentChips.js'

/** The reply being written right now (text so far + compact tool rows). */
function LiveReply({ runId }: { runId: string }) {
  const view = useRunTranscript(runId)
  if (view.status !== 'running') return null
  const last = [...view.entries].reverse().find((e) => e.kind === 'turn')
  if (!last || last.kind !== 'turn' || last.turn.state !== 'streaming') return null
  const text = last.turn.items
    .filter((i): i is TextItem => i.type === 'text')
    .map((i) => i.text)
    .join('\n\n')
  const tools = last.turn.items.filter((i) => i.type === 'tool').slice(-3)
  return (
    <li className="grid gap-1" aria-busy="true" data-live-reply>
      {tools.map((t) =>
        t.type === 'tool' ? (
          <span key={t.id} className="text-[14px] text-text-3">
            ⋯ {t.title}
          </span>
        ) : null,
      )}
      <div className="max-w-[85%] rounded-xl bg-surface-2 px-3 py-2 text-[15px] text-text-1">
        {text ? <Markdown text={text} /> : <span className="text-text-3">Thinking…</span>}
      </div>
    </li>
  )
}

export interface ConversationProps {
  bark: BarkRecord
  store: BarkStore
  /** Open another item (a split child or parent). */
  onOpenItem: (barkId: string) => void
  /** Collapsed to the last two messages (a quick bark), with "Continue the conversation". */
  collapsed?: boolean
  /** segmentation: other items of the project, for "This belongs to…". */
  items?: { id: string; title: string }[]
}

export function Conversation({
  bark,
  store,
  onOpenItem,
  collapsed = false,
  items = [],
}: ConversationProps) {
  const toast = useToast()
  const id = useId()
  const [open, setOpen] = useState(!collapsed)
  const [draft, setDraft] = useState('')
  const [newTopic, setNewTopic] = useState(false)
  const [busy, setBusy] = useState(false)
  const input = useAttachmentInput({
    storageOff: storage.off,
    largeTextBytes: LARGE_TEXT_BYTES,
    insertText: (t) => setDraft((cur) => cur + t),
  })
  const dictation = useDictation((t) =>
    setDraft((cur) => (cur.trim() ? `${cur.replace(/\s+$/, '')} ${t}` : t)),
  )
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    try {
      await fn()
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setBusy(false)
    }
  }

  const send = () =>
    run(async () => {
      const text = draft.trim()
      if (!text && !input.files.length) return
      const proposal = latestOpenProposal(bark)
      if (proposal && isDoIt(text) && !input.files.length) {
        await store.confirm(bark, proposal)
      } else if ((newTopic || startsNewTopic(text)) && bark.thread.length > 1) {
        // A new topic becomes its own item with a clean context (the split is undoable).
        const msgId = `u-${crypto.randomUUID().slice(0, 12)}`
        const saved = await store.update(bark, (cur) => ({
          ...cur,
          thread: [...cur.thread, { id: msgId, at: Date.now(), author: 'user' as const, text }],
        }))
        const r = (await store.control(saved, 'split', { messageId: msgId })) as
          | { result?: { newItemId?: string } }
          | undefined
        const child = r?.result?.newItemId
        if (child) onOpenItem(child)
      } else {
        input.markUploading(true)
        await store.message(bark, text, { files: input.files })
      }
      setDraft('')
      setNewTopic(false)
      input.clear()
    }).finally(() => {
      input.retryable()
      input.markUploading(false)
    })

  const messages = open ? bark.thread : bark.thread.slice(-2)
  const actions = actionList(bark)
  const proposal = (m: BarkThreadMessage) =>
    m.proposalId ? bark.proposals.find((p) => p.id === m.proposalId) : undefined

  return (
    <section aria-labelledby={`${id}-h`} className="grid gap-3">
      <h3 id={`${id}-h`} className="m-0 text-[15px] font-semibold text-text-1">
        Conversation
      </h3>
      {bark.splitFrom ? (
        <p className="m-0 text-[14px] text-text-2">
          Split from{' '}
          <button
            type="button"
            className="text-accent-text underline"
            onClick={() => onOpenItem(bark.splitFrom!.itemId)}
          >
            the earlier conversation
          </button>
        </p>
      ) : null}
      {actions.length ? (
        <p
          className="m-0 rounded-lg bg-surface-2 px-3 py-2 text-[14px] text-text-2"
          data-action-list
        >
          Actions from this conversation:{' '}
          {actions.map((a, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: derived list, order is the identity
            <span key={i}>
              {i ? ' · ' : ''}
              {a.glyph} {a.text}
            </span>
          ))}
        </p>
      ) : null}
      <ol className="m-0 grid list-none gap-2 p-0" aria-live="polite">
        {messages.map((m) => {
          const p = proposal(m)
          if (m.author === 'system')
            return (
              <li
                key={m.id}
                className="flex flex-wrap items-center gap-2 text-[14px] text-text-3"
                data-divider={m.segmentChip}
              >
                <span className="h-px flex-1 bg-border-subtle" aria-hidden="true" />
                {m.segmentChip === 'split-done' && m.movedTo ? (
                  <>
                    <button
                      type="button"
                      className="text-accent-text"
                      onClick={() => onOpenItem(m.movedTo!)}
                    >
                      {m.text} →
                    </button>
                    <button
                      type="button"
                      className="text-text-2 underline"
                      disabled={busy}
                      onClick={() =>
                        void run(() => store.control(bark, 'merge', { targetItemId: m.movedTo! }))
                      }
                    >
                      Undo split
                    </button>
                  </>
                ) : m.segmentChip === 'suggest-split' || m.segmentChip === 'continue-or-new' ? (
                  <SegmentChip
                    bark={bark}
                    chip={m}
                    store={store}
                    busy={busy}
                    run={run}
                    onOpenItem={onOpenItem}
                  />
                ) : (
                  <span>{m.text}</span>
                )}
                <span className="h-px flex-1 bg-border-subtle" aria-hidden="true" />
              </li>
            )
          return (
            <li
              key={m.id}
              className={`group grid gap-1 ${m.author === 'user' ? 'justify-items-end' : ''}`}
              data-message={m.id}
            >
              <div
                className={`max-w-[85%] rounded-xl px-3 py-2 text-[15px] ${
                  m.author === 'user' ? 'bg-accent-soft text-text-1' : 'bg-surface-2 text-text-1'
                }`}
              >
                {m.author === 'agent' ? (
                  <Markdown text={m.text} />
                ) : (
                  <span className="whitespace-pre-wrap">{m.text}</span>
                )}
                {m.attachments?.length ? (
                  <span className="mt-1 block text-[14px] text-text-3">
                    {m.attachments.length} attachment{m.attachments.length === 1 ? '' : 's'}
                  </span>
                ) : null}
              </div>
              {p ? (
                <div className="w-full max-w-[520px]">
                  <ProposalCard
                    proposal={p}
                    busy={busy}
                    onDo={() => void run(() => store.confirm(bark, p))}
                    onAdjust={(ops) => void run(() => store.confirm(bark, p, ops))}
                    onKeepTalking={() =>
                      void run(() => store.control(bark, 'decline', { proposalId: p.id }))
                    }
                  />
                </div>
              ) : null}
              {m.author === 'user' && bark.thread[0]?.id !== m.id && bark.mode === 'discuss' ? (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-[14px] text-text-3 opacity-70 group-hover:opacity-100 focus:opacity-100"
                  disabled={busy}
                  onClick={() =>
                    void run(async () => {
                      const r = (await store.control(bark, 'split', { messageId: m.id })) as
                        | { result?: { newItemId?: string } }
                        | undefined
                      if (r?.result?.newItemId) onOpenItem(r.result.newItemId)
                    })
                  }
                >
                  <Scissors size={12} aria-hidden="true" /> Split into new item from here
                </button>
              ) : null}
              {m.author === 'user' && bark.thread[0]?.id !== m.id ? (
                <BelongsTo
                  bark={bark}
                  message={m}
                  items={items}
                  store={store}
                  busy={busy}
                  run={run}
                  onOpenItem={onOpenItem}
                />
              ) : null}
            </li>
          )
        })}
        {bark.triage.itemRunId ? <LiveReply runId={bark.triage.itemRunId} /> : null}
      </ol>
      {!open ? (
        <Button size="sm" onClick={() => setOpen(true)}>
          Continue the conversation
        </Button>
      ) : (
        <AttachmentDropZone input={input} className="grid gap-2">
          <label className="sr-only" htmlFor={`${id}-composer`}>
            Message Viko about this
          </label>
          <textarea
            id={`${id}-composer`}
            rows={2}
            value={dictation.interim ? `${draft} ${dictation.interim}` : draft}
            placeholder={
              newTopic
                ? 'New topic: this starts a new item'
                : latestOpenProposal(bark)
                  ? 'Reply, or type “do it” to confirm the proposal'
                  : 'Add context, ask, or keep exploring'
            }
            className="w-full resize-y rounded-lg border border-border-subtle bg-surface-1 p-2 text-[15px] text-text-1"
            onChange={(e) => setDraft(e.target.value)}
            onPaste={input.onPaste}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault()
                void send()
              }
              if (e.key === 'Escape') e.currentTarget.blur()
            }}
          />
          <div className="flex flex-wrap items-center gap-2">
            {dictation.supported ? (
              <Button
                size="sm"
                variant="ghost"
                aria-pressed={dictation.listening}
                aria-label={dictation.listening ? 'Stop dictation' : 'Dictate'}
                icon={dictation.listening ? <MicOff size={14} /> : <Mic size={14} />}
                onClick={dictation.toggle}
              />
            ) : null}
            <Button
              size="sm"
              variant="ghost"
              aria-pressed={newTopic}
              onClick={() => setNewTopic(!newTopic)}
            >
              New topic
            </Button>
            <span className="flex-1" />
            <Button
              size="sm"
              variant="primary"
              icon={<Send size={14} />}
              disabled={busy || (!draft.trim() && !input.files.length)}
              onClick={() => void send()}
            >
              Send
            </Button>
          </div>
        </AttachmentDropZone>
      )}
    </section>
  )
}
